import React, { useContext, useRef, useState } from 'react';
import { Alert, Box, Button, Collapse, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import IMedicalCertificate from '../../interfaces/IMedicalCertificate';
import { FaRegEdit } from 'react-icons/fa';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { Download } from '@mui/icons-material';
import { useMedicalCertificateService } from '../../hooks/services/useMedicalCertificateService';
import {
  EVENTOS_WIDTH,
  getBaseCellProps,
  getHighlightCellProps,
  getStickyProps,
  getStickyRightProps,
  HeaderConfig,
} from '../../hooks/util/useStickTable';
import { useScrollContainerWidth } from '../../hooks/util/useScrollContainerWidth';
import { hasAccessToAction } from '../../util/hasAccessTo';
import UserContext from '../../context/user/UserContext';
import EventTimeline from './EventTimeline';
import FollowupEventsDialog from './FollowupEventsDialog';

interface IRowProps {
  medicalCertificateRow: IMedicalCertificate;
  handleClick: (medicalCertificate: IMedicalCertificate) => void;
  seeCID: boolean;
  headers: HeaderConfig[];
}
interface DownloadResource {
  medicalCertificateid: string;
  filename: string;
  token: string;
  description: string;
}

interface Attachment {
  id: string;
  medicalCertificateId: string;
  attachment: string;
}

const INLINE_EVENTS_LIMIT = 5;

const FollowupListRow = (props: IRowProps, indx: number) => {
  const [openEvent, setOpenEvent] = React.useState(false);
  const [openEventsDialog, setOpenEventsDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const medicalCertificateRequest = useMedicalCertificateService();
  const medicalCertificateRow = props.medicalCertificateRow;
  const { state } = useContext(UserContext);

  const panelCellRef = useRef<HTMLTableCellElement>(null);
  const seeAllButtonRef = useRef<HTMLButtonElement>(null);
  const panelWidth = useScrollContainerWidth(panelCellRef, openEvent);

  const events = medicalCertificateRow.events ?? [];
  const hasMoreEvents = events.length > INLINE_EVENTS_LIMIT;
  const panelId = `followup-events-panel-${medicalCertificateRow.id ?? ''}`;
  const highlightProps = openEvent ? getHighlightCellProps() : {};

  const canView = hasAccessToAction(
    ["followup-atestado-web"],
    state.resourcesWithActions,
    "Editar"
  );

  const getFilename = (attachment: Attachment) => {
    const url = attachment.attachment;
    const fileName = url.split('/')[url.split('/').length - 1];
    return fileName.substring(33, 999);
  };

  const handleDownloadAll = async () => {
    const attachments = medicalCertificateRow?.attachments || [];
    const parsedStorage = JSON.parse(sessionStorage.getItem('user') || '{}');
    const token = parsedStorage.access_token || '';

    for (const att of attachments) {
      if (!att.attachment) continue;

      try {
        const resource: DownloadResource = {
          medicalCertificateid: att.medicalCertificateId,
          filename: att.attachment,
          token: token,
          description: getFilename(att),
        };

        await handleDownload(resource);

      } catch (error) {
        setError(error instanceof Error ? error.message : 'Ocorreu um erro ao baixar o arquivo.');
      }

    }
  };

  const handleDownload = async (attachment: DownloadResource) => {
    try {
      const result = await medicalCertificateRequest.getDownloadImage(
        attachment.medicalCertificateid,
        attachment.filename,
        attachment.token,
        attachment.description,
      );
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.description
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Ocorreu um erro ao baixar o arquivo.');
    }
  };

  const handleCloseEventsDialog = () => {
    setOpenEventsDialog(false);
    seeAllButtonRef.current?.focus();
  };

  return (
    <>
      <TableRow key={`${indx}`}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TableCell
          sx={{
            ...getBaseCellProps(),
            ...highlightProps,
            position: 'sticky',
            left: 0,
            zIndex: 2,
            width: EVENTOS_WIDTH,
            minWidth: EVENTOS_WIDTH,
          }}
        >
          <IconButton
            aria-label={`${openEvent ? 'Recolher' : 'Expandir'} eventos do documento ${medicalCertificateRow.protocolNumber ?? ''}`.trim()}
            aria-expanded={openEvent}
            aria-controls={panelId}
            size="small"
            onClick={() => setOpenEvent(!openEvent)}
          >
            {openEvent ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {props.headers.map((head, index) => (
          <TableCell key={head.field} sx={{ ...getStickyProps(props.headers, index), ...highlightProps }}>
            {head.render
              ? head.render(medicalCertificateRow)
              : (medicalCertificateRow as Record<string, any>)[head.field] ?? ''}
          </TableCell>
        ))}

        <TableCell sx={{ ...getBaseCellProps(), ...highlightProps }}>
          {medicalCertificateRow?.alerts?.length !== 0 ? 'Sim' : 'Não'}
        </TableCell>

        <TableCell sx={{ ...getStickyRightProps(), ...highlightProps }}>
          <Tooltip title="Editar" placement="left">
            <IconButton color="info" onClick={() => props.handleClick(medicalCertificateRow)}>
              <FaRegEdit />
            </IconButton>
          </Tooltip>
          {medicalCertificateRow?.attachments && medicalCertificateRow?.attachments?.length > 0 && (
            <Tooltip title={`Baixar ${medicalCertificateRow.attachments.length} anexo(s)`} placement="left">
              <IconButton color="info" onClick={handleDownloadAll} size="medium" disabled={!canView}>
                <Download />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>

      </TableRow>
      <TableRow>
        <TableCell
          ref={panelCellRef}
          colSpan={props.headers.length + 3}
          sx={{ padding: 0, borderBottom: openEvent ? undefined : 'none' }}
        >
          <Collapse in={openEvent} timeout="auto" unmountOnExit>
            <Box
              id={panelId}
              sx={{
                position: 'sticky',
                left: 0,
                boxSizing: 'border-box',
                width: panelWidth ? `${panelWidth}px` : '100%',
                maxWidth: panelWidth ? `${panelWidth}px` : '100%',
                padding: '12px 16px 16px 16px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  minHeight: 30,
                  marginBottom: 1,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                  Últimos eventos
                </Typography>
                {hasMoreEvents && (
                  <Button
                    ref={seeAllButtonRef}
                    size="small"
                    variant="text"
                    sx={{ textTransform: 'none', fontSize: 12 }}
                    onClick={() => setOpenEventsDialog(true)}
                  >
                    {`Ver todos os ${events.length}`}
                  </Button>
                )}
              </Box>

              {events.length > 0 ? (
                <EventTimeline events={events} limit={INLINE_EVENTS_LIMIT} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, paddingBottom: 0.5 }}>
                  <HistoryToggleOffIcon sx={{ fontSize: 20, color: 'text.disabled', marginTop: '1px' }} />
                  <Box>
                    <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: 'text.primary' }}>
                      Nenhum evento registrado ainda
                    </Typography>
                    <Typography sx={{ fontSize: 11, lineHeight: 1.5, color: 'text.secondary' }}>
                      Os eventos aparecem aqui quando o documento for analisado ou integrado.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <FollowupEventsDialog
        open={openEventsDialog}
        onClose={handleCloseEventsDialog}
        events={events}
        protocolNumber={medicalCertificateRow.protocolNumber}
        employeeName={medicalCertificateRow.employee?.name}
      />
    </>
  );
};

export default FollowupListRow;
